import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useWebSocket } from './WebSocketContext';
import websocketService from '../services/websocketService';
import { fetchPendingChatRequests } from '../api/chatService';
import { extractUserInfo } from '../utils/jwtDecoder';
import {
  mapPendingApiRow,
  normalizeChatRequestFromNotification,
  normalizeChatRequestFromWebSocket,
  parseChatAcceptedFromMessage
} from '../utils/agentChatRequestNormalize';
import { isAuthenticatedAgentStaff } from '../utils/agentRoleGuard';

const AgentLiveChatContext = createContext(null);

const ELSEWHERE_ROW_TTL_MS = 45000;

export function AgentLiveChatProvider({ children }) {
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [activeChatSession, setActiveChatSession] = useState(null);
  const { isConnected } = useWebSocket();
  const elsewhereTimersRef = useRef(new Map());

  const clearElsewhereTimer = useCallback((chatSessionId) => {
    if (chatSessionId == null) return;
    const id = String(chatSessionId);
    const t = elsewhereTimersRef.current.get(id);
    if (t) clearTimeout(t);
    elsewhereTimersRef.current.delete(id);
  }, []);

  const addIncomingRequest = useCallback((req) => {
    if (!req?.chatSessionId) return;
    const id = String(req.chatSessionId);
    setIncomingRequests((prev) => {
      const idx = prev.findIndex((r) => String(r.chatSessionId) === id);
      if (idx >= 0 && prev[idx].queueState === 'accepted_elsewhere') {
        return prev;
      }
      if (prev.some((r) => String(r.chatSessionId) === id)) {
        return prev;
      }
      return [...prev, { ...req, chatSessionId: req.chatSessionId }];
    });
  }, []);

  const removeIncomingRequest = useCallback((chatSessionId) => {
    if (chatSessionId == null) return;
    const id = String(chatSessionId);
    clearElsewhereTimer(chatSessionId);
    setIncomingRequests((prev) => prev.filter((r) => String(r.chatSessionId) !== id));
  }, [clearElsewhereTimer]);

  const replaceIncomingRequests = useCallback((list) => {
    const next = Array.isArray(list) ? list.filter(Boolean) : [];
    elsewhereTimersRef.current.forEach((t) => clearTimeout(t));
    elsewhereTimersRef.current.clear();
    setIncomingRequests(next);
  }, []);

  const clearActiveChat = useCallback(() => {
    setActiveChatSession(null);
  }, []);

  const refreshPendingFromApi = useCallback(async () => {
    if (!isAuthenticatedAgentStaff()) {
      return;
    }
    try {
      const raw = await fetchPendingChatRequests();
      const mapped = raw.map(mapPendingApiRow).filter(Boolean);
      replaceIncomingRequests(mapped);
    } catch (e) {
      console.warn('[AgentLiveChat] Pending requests fetch failed:', e?.message || e);
    }
  }, [replaceIncomingRequests]);

  const scheduleRemoveElsewhereRow = useCallback(
    (chatSessionId) => {
      if (chatSessionId == null) return;
      const id = String(chatSessionId);
      clearElsewhereTimer(chatSessionId);
      const t = setTimeout(() => {
        setIncomingRequests((prev) => prev.filter((r) => String(r.chatSessionId) !== id));
        elsewhereTimersRef.current.delete(id);
      }, ELSEWHERE_ROW_TTL_MS);
      elsewhereTimersRef.current.set(id, t);
    },
    [clearElsewhereTimer]
  );

  const markChatAcceptedElsewhere = useCallback(
    (message) => {
      const parsed = parseChatAcceptedFromMessage(message);
      if (!parsed?.chatSessionId) return;

      const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
      const me = extractUserInfo(token)?.userId;
      if (
        me != null &&
        parsed.assignedAgentId != null &&
        String(parsed.assignedAgentId) === String(me)
      ) {
        removeIncomingRequest(parsed.chatSessionId);
        return;
      }

      const name = parsed.acceptedByAgentName || 'Another agent';
      setIncomingRequests((prev) => {
        const id = String(parsed.chatSessionId);
        const idx = prev.findIndex((r) => String(r.chatSessionId) === id);
        const patch = {
          queueState: 'accepted_elsewhere',
          acceptedByAgentName: name,
          statusLabel: 'Accepted / In progress',
          acceptedElsewhereAt: Date.now()
        };
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...patch };
          return next;
        }
        return [
          ...prev,
          {
            chatSessionId: parsed.chatSessionId,
            guestName: parsed.guestName || 'Guest',
            subject: parsed.subject || 'Live chat',
            ticketNumber: parsed.ticketNumber,
            email: undefined,
            status: parsed.status || 'ACTIVE',
            createdOn: new Date().toISOString(),
            timestamp: Date.now(),
            ...patch
          }
        ];
      });
      scheduleRemoveElsewhereRow(parsed.chatSessionId);
    },
    [removeIncomingRequest, scheduleRemoveElsewhereRow]
  );

  /** WebSocket: chat_request + chat_accepted (only agent staff; survives route changes inside Home) */
  useEffect(() => {
    if (!isConnected || !isAuthenticatedAgentStaff()) {
      return undefined;
    }

    const handler = (message) => {
      const nt = message.notificationType || message.type;
      if (nt === 'chat_accepted') {
        markChatAcceptedElsewhere(message);
        return;
      }

      const incoming = normalizeChatRequestFromWebSocket(message);
      if (incoming) {
        addIncomingRequest(incoming);
      }
    };

    websocketService.subscribe('chat_request', handler);
    websocketService.subscribe('chat_accepted', handler);
    refreshPendingFromApi();

    return () => {
      websocketService.unsubscribe('chat_request', handler);
      websocketService.unsubscribe('chat_accepted', handler);
    };
  }, [isConnected, addIncomingRequest, markChatAcceptedElsewhere, refreshPendingFromApi]);

  /** Bell / NotificationContext → same queue (agents only) */
  useEffect(() => {
    const onBellAccepted = (e) => {
      if (!isAuthenticatedAgentStaff()) return;
      if (e?.detail) markChatAcceptedElsewhere(e.detail);
    };
    window.addEventListener('hd:agent-chat-accepted', onBellAccepted);
    return () => window.removeEventListener('hd:agent-chat-accepted', onBellAccepted);
  }, [markChatAcceptedElsewhere]);

  /** Tab focus: align with server (agents only) */
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && isAuthenticatedAgentStaff()) {
        refreshPendingFromApi();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refreshPendingFromApi]);

  /** FCM / NotificationContext → pending row (agents only) */
  useEffect(() => {
    const onBellSync = (e) => {
      if (!isAuthenticatedAgentStaff()) return;
      const row = normalizeChatRequestFromNotification(e.detail);
      if (row) addIncomingRequest(row);
    };
    window.addEventListener('hd:agent-chat-request', onBellSync);
    return () => window.removeEventListener('hd:agent-chat-request', onBellSync);
  }, [addIncomingRequest]);

  useEffect(
    () => () => {
      elsewhereTimersRef.current.forEach((t) => clearTimeout(t));
      elsewhereTimersRef.current.clear();
    },
    []
  );

  const value = useMemo(
    () => ({
      incomingRequests,
      activeChatSession,
      setActiveChatSession,
      clearActiveChat,
      addIncomingRequest,
      removeIncomingRequest,
      setIncomingRequests: replaceIncomingRequests,
      refreshPendingFromApi
    }),
    [
      incomingRequests,
      activeChatSession,
      clearActiveChat,
      addIncomingRequest,
      removeIncomingRequest,
      replaceIncomingRequests,
      refreshPendingFromApi
    ]
  );

  return (
    <AgentLiveChatContext.Provider value={value}>{children}</AgentLiveChatContext.Provider>
  );
}

export function useAgentLiveChat() {
  const ctx = useContext(AgentLiveChatContext);
  if (!ctx) {
    throw new Error('useAgentLiveChat must be used within AgentLiveChatProvider');
  }
  return ctx;
}
